import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'wasp/client/auth';
import { isAdministrator } from '../../client/lib/platformRole';
import { updateLocation, getLocationById, getLocationTypes, useQuery, deleteLocation, useAction } from 'wasp/client/operations';
import { type Location } from 'wasp/entities';
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
  ActionButtonDanger
} from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { AlertCircle, LoaderCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { CANADIAN_PROVINCE_OPTIONS, COUNTRY_CANADA } from './canadianProvinces';
import LocationContentApprovalSettingsCard from './LocationContentApprovalSettingsCard';

const EditLocationPage = () => {
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ submit?: string }>({});
  const deleteLocationFn = useAction(deleteLocation);
  
  const [formData, setFormData] = useState({
    name: '',
    province: '' as string,
    country: COUNTRY_CANADA,
    locationTypeId: '' as string | null,
    isPublic: false,
    allowPublicSubscription: true,
    defaultVisibility: 'VERIFIED',
    timezone: 'UTC'
  });

  // Get location data
  const { data: location, isLoading: loadingLocation } = useQuery(getLocationById,
    { id: id! },
    { enabled: !!id }
  );

  // Fetch location types from database
  const { data: locationTypes = [] } = useQuery(getLocationTypes);

  useEffect(() => {
    if (location) {
      const loc = location as Location;
      setFormData({
        name: loc.name || '',
        province: (loc as any).province || '',
        country: (loc as any).country || COUNTRY_CANADA,
        locationTypeId: (loc as any).locationTypeId || null,
        isPublic: loc.isPublic || false,
        allowPublicSubscription: loc.allowPublicSubscription ?? true,
        defaultVisibility: loc.defaultVisibility || 'VERIFIED',
        timezone: loc.timezone || 'UTC'
      });
    }
  }, [location]);

  const validateForm = (): boolean => {
    const newErrors: { submit?: string } = {};
    if (!formData.name.trim()) {
      newErrors.submit = 'Location name is required';
    }
    if (!formData.province) {
      newErrors.submit = 'Province is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const locationData = {
        id,
        ...formData
      };

      await updateLocation(locationData);
      toast({
        title: "Success",
        description: "Location updated successfully"
      });
      navigate('/admin/taxonomy/location-names');
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to update location. Please try again.';
      setErrors({ submit: errorMessage });
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!location) return;
    if (!confirm(`Are you sure you want to delete "${(location as any).name}"?`)) return;

    try {
      await deleteLocationFn({ id: (location as any).id });
      toast({
        title: "Success",
        description: "Location deleted successfully"
      });
      navigate('/admin/taxonomy/location-names');
    } catch (err: any) {
      console.error('Error deleting location:', err);
      toast({
        title: "Error",
        description: err.message || "Failed to delete location",
        variant: "destructive"
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear submit error when user starts typing
    if (errors.submit) {
      setErrors({});
    }
  };

  if (!user || !isAdministrator(user)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">Admin privileges required.</p>
        </div>
      </div>
    );
  }

  if (loadingLocation) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoaderCircle className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading location...</p>
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Location Not Found</h1>
          <p className="text-gray-600 mb-4">The location you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/taxonomy/location-names')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Locations
          </button>
        </div>
      </div>
    );
  }

  const sidebarContent = (
    <>
      <SidebarSection isFirst>
        <ActionButtons>
          <ActionButton
            type="submit"
            form="location-form"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </ActionButton>
          <ActionButtonSecondary
            onClick={() => navigate('/admin/taxonomy/location-names')}
          >
            Cancel
          </ActionButtonSecondary>
          <ActionButtonDanger
            onClick={handleDelete}
          >
            Delete
          </ActionButtonDanger>
        </ActionButtons>
      </SidebarSection>

      <SidebarSection title="Location Info">
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-gray-600 font-medium mb-1">Code</p>
            <p className="font-mono text-gray-900">{(location as any).locationCode}</p>
          </div>
          {(locationTypes as any[]).find((lt: any) => lt.id === formData.locationTypeId) && (
            <div>
              <p className="text-gray-600 font-medium mb-1">Type</p>
              <p className="text-gray-900">{(locationTypes as any[]).find((lt: any) => lt.id === formData.locationTypeId)?.label}</p>
            </div>
          )}
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      sidebarContent={sidebarContent}
      layoutVariant="taxonomy"
      authUser={user}
    >
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{(location as any).name}</h1>
          <p className="text-gray-600">Update location settings and information</p>
        </div>

        {/* Error Message */}
        {errors.submit && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-red-900">{errors.submit}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} id="location-form" className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Location Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter location name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="province">Province *</Label>
                <Select
                  value={formData.province || 'none'}
                  onValueChange={(value) => handleInputChange('province', value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select province" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select province</SelectItem>
                    {CANADIAN_PROVINCE_OPTIONS.map((province) => (
                      <SelectItem key={province.value} value={province.value}>{province.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value="Canada"
                  disabled
                  readOnly
                />
              </div>
              
              <div>
                <Label htmlFor="locationTypeId">Location Type</Label>
                <Select
                  value={formData.locationTypeId || 'none'}
                  onValueChange={(value) => handleInputChange('locationTypeId', value === 'none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {(locationTypes as any[]).filter((lt: any) => lt.isActive).map((lt: any) => (
                      <SelectItem key={lt.id} value={lt.id}>{lt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Location Settings */}
          <Card>
            <CardHeader>
              <CardTitle>Location Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="isPublic">Public Location</Label>
                  <p className="text-sm text-gray-600">Allow public visitors to view this location</p>
                </div>
                <Switch
                  id="isPublic"
                  checked={formData.isPublic}
                  onCheckedChange={(checked) => handleInputChange('isPublic', checked)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="allowPublicSubscription">Allow Public Subscription</Label>
                  <p className="text-sm text-gray-600">Allow users to subscribe without invitation</p>
                </div>
                <Switch
                  id="allowPublicSubscription"
                  checked={formData.allowPublicSubscription}
                  onCheckedChange={(checked) => handleInputChange('allowPublicSubscription', checked)}
                />
              </div>
              
              <div>
                <Label htmlFor="defaultVisibility">Default Content Visibility</Label>
                <Select value={formData.defaultVisibility} onValueChange={(value) => handleInputChange('defaultVisibility', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PUBLIC">Public</SelectItem>
                    <SelectItem value="SUBSCRIBER">Subscribers Only</SelectItem>
                    <SelectItem value="VERIFIED">Verified Members Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Timezone */}
          <Card>
            <CardHeader>
              <CardTitle>Timezone</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={formData.timezone} onValueChange={(value) => handleInputChange('timezone', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="Europe/London">London</SelectItem>
                    <SelectItem value="Europe/Paris">Paris</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                    <SelectItem value="Asia/Shanghai">Shanghai</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {id && <LocationContentApprovalSettingsCard locationId={id} />}
        </form>
      </div>
    </IndividualEditPageLayout>
  );
};

export default EditLocationPage;



